import { useState } from 'react';
import { toast, type Destructive } from '@drbell/ui';
import {
  MAX_ANCHORS, linkRefusal, linkableMedicines, linksAt, medicineTitle, planLink, planUnlink, relationsFor,
  type AnchorKey, type LinkRefusal, type Relation, type TimeLink,
} from '@drbell/core';
import { api, type MedicationEntry, type RoutineTime } from '../api.ts';
import { anchorLine, countLine } from '../self-service.ts';

/**
 * The medicines taken at one time of day, on the Routine tab — the phone's
 * "Link a medicine" (apps/mobile RoutineLinks.tsx), on the website.
 *
 * Under each meal and each of the person's own times: what is taken then
 * ("Dolo 650 · after"), **+ Link a medicine** (a medicine, then before /
 * with / after; Bedtime is "at bedtime" only), and **Unlink**. Each says
 * what changes before anything is saved — "Dolo 650 will be taken 3 times
 * a day instead of 2" — and never leaves a medicine with no time: its last
 * one offers to stop the medicine instead, in the medicine's own words.
 *
 * The arithmetic is the phone's own (`planLink` / `planUnlink` in
 * @drbell/core), and saving is the medicine's own change of times,
 * `PUT …/schedule`: the same route, validation, moved-dose rules and
 * statement line as changing the medicine. Only for whoever may edit
 * medicines; everyone who may read them sees the rows.
 */
const REL_WORD: Record<Relation, string> = { before: 'before', with: 'with', after: 'after' };
const REL_BUTTON: Record<Relation, (time: string) => string> = {
  before: (t) => `Before ${t}`, with: (t) => `With ${t}`, after: (t) => `After ${t}`,
};
export const REFUSAL: Record<LinkRefusal, string> = {
  not_meal: 'Taken at set clock times, not with meals. Change its times in Medicines.',
  change_waiting: 'A change of its times starts soon. Change it in Medicines.',
  too_many: `Already taken ${MAX_ANCHORS} times a day, the most there can be.`,
  not_running: 'Taken at set clock times, not with meals. Change its times in Medicines.',
};

/** "Dolo 650 · after"; at Bedtime the name alone, which is all "at bedtime" says there. */
export const linkText = (link: TimeLink, key: AnchorKey) =>
  key === 'bedtime' ? link.title : `${link.title} · ${REL_WORD[link.relation]}`;

export function TimeMedicines({
  patientId, anchorKey, timeName, entries, ownTimes, today, canEdit, onAsk, onOpen, onChanged,
}: {
  patientId: string;
  /** `breakfast` … `bedtime`, or `time:<id>`. */
  anchorKey: AnchorKey;
  /** As the person reads it: "Lunch", or their own "Evening tea". */
  timeName: string;
  /** This person's medicines (not diet items). */
  entries: readonly MedicationEntry[];
  ownTimes: readonly RoutineTime[];
  /** The person's own calendar day. */
  today: string;
  /** May edit medicines (`medication.edit`). */
  canEdit: boolean;
  /** Show a confirmation (the page's one Confirm dialog). */
  onAsk: (ask: Destructive) => void;
  /** Open the medicine in Medicines. */
  onOpen: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const [picking, setPicking] = useState<null | 'medicine' | MedicationEntry>(null);
  const links = linksAt(entries, anchorKey, today);
  const offered = linkableMedicines(entries, today);

  /** Saved: the medicines reload, and the reminders follow from today. Failure shows in the dialog. */
  const save = async (title: string, work: () => Promise<unknown>) => {
    await work();
    setPicking(null);
    toast.success(title, `Saved. ${title}’s reminders follow from today.`);
    await onChanged();
  };

  const link = (entry: MedicationEntry, relation: Relation) => {
    const title = medicineTitle(entry.medication);
    const plan = planLink(entry, anchorKey, relation, today);
    if (!plan.ok) {
      toast.info(title, REFUSAL[plan.reason]);
      return;
    }
    // Exactly as it is already: nothing to save.
    if (plan.kind === 'same') {
      setPicking(null);
      return;
    }
    onAsk({
      title: `Link ${title} to ${timeName}?`,
      body: `${countLine(title, plan.timesBefore, plan.timesAfter)} At ${timeName}: ${anchorLine(plan.anchor, ownTimes)}.`,
      reassurance: 'From today. A dose already taken today stays as it is.',
      confirmLabel: plan.kind === 'add' ? 'Link' : 'Save',
      tone: 'primary',
      onConfirm: () => save(title, () => api.put(`/api/patients/${patientId}/medications/${entry.medication.id}/schedule`, plan.body)),
    });
  };

  const unlink = (medicationId: string) => {
    const entry = entries.find((e) => e.medication.id === medicationId);
    if (!entry) return;
    const title = medicineTitle(entry.medication);
    const plan = planUnlink(entry, anchorKey, today);
    if (plan.ok) {
      onAsk({
        title: `Unlink ${title} from ${timeName}?`,
        body: countLine(title, plan.timesBefore, plan.timesAfter),
        reassurance: 'From today. A dose already taken today stays as it is.',
        confirmLabel: 'Unlink',
        onConfirm: () => save(title, () => api.put(`/api/patients/${patientId}/medications/${medicationId}/schedule`, plan.body)),
      });
      return;
    }
    if (plan.reason === 'last') {
      // Its only time: never left with none. Stopping is the medicine's own stop, said its own way.
      onAsk({
        title: `${title} has no other time`,
        body: 'A medicine needs at least one time of day, or it would never remind anyone. To take it at another time, link it there first. If the doctor has stopped it, stop it here.',
        reassurance: 'Reminders for it stop now, on the phone and for the caregivers. What has already been recorded is kept. Only stop a medicine when the doctor has said to.',
        confirmLabel: 'Stop taking this',
        onConfirm: () => save(title, () => api.del(`/api/patients/${patientId}/medications/${medicationId}`)),
      });
      return;
    }
    if (plan.reason !== 'not_linked') toast.info(title, REFUSAL[plan.reason]);
  };

  return (
    <div className="mt-2 space-y-1">
      {links.map((l, i) => (
        <div key={`${l.medicationId}-${i}`} className="flex flex-wrap items-center gap-2">
          <button
            onClick={onOpen}
            title="Opens it in Medicines"
            className="min-h-11 flex-1 text-left text-sm hover:text-(--accent)"
          >
            <span aria-hidden="true">💊 </span>{linkText(l, anchorKey)}
          </button>
          {canEdit && (
            <button
              onClick={() => unlink(l.medicationId)}
              aria-label={`Unlink ${l.title} from ${timeName}`}
              className="min-h-11 rounded-lg px-2 text-sm font-medium text-(--accent) hover:underline"
            >
              Unlink
            </button>
          )}
        </div>
      ))}

      {!canEdit || offered.length === 0 ? null : picking === null ? (
        <button onClick={() => setPicking('medicine')} className="min-h-11 text-sm font-medium text-(--accent) hover:underline">
          + Link a medicine
        </button>
      ) : picking === 'medicine' ? (
        <div className="space-y-2 rounded-lg bg-(--accent-soft) p-3">
          <p className="text-sm font-medium">Which medicine is taken at {timeName}?</p>
          {offered.map((entry) => {
            const title = medicineTitle(entry.medication);
            const refused = linkRefusal(entry, today);
            const here = links.some((l) => l.medicationId === entry.medication.id);
            const hint = refused ? REFUSAL[refused] : here ? 'Taken at this time now' : null;
            return (
              <button
                key={entry.medication.id}
                disabled={!!refused}
                onClick={() => {
                  // Bedtime has only "at bedtime": straight to the confirmation.
                  const only = relationsFor(anchorKey);
                  if (only.length === 1) link(entry, only[0]!);
                  else setPicking(entry);
                }}
                className="block min-h-11 w-full rounded-lg border-2 border-(--line) bg-(--bg) px-3 py-2 text-left text-sm hover:border-(--accent) disabled:opacity-55"
              >
                {title}
                {hint && <span className="block text-xs text-(--muted)">{hint}</span>}
              </button>
            );
          })}
          <button onClick={() => setPicking(null)} className="min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3 text-sm">
            Cancel
          </button>
        </div>
      ) : (
        <div className="space-y-2 rounded-lg bg-(--accent-soft) p-3">
          <p className="text-sm font-medium">When is {medicineTitle(picking.medication)} taken?</p>
          <div className="flex flex-wrap gap-2">
            {relationsFor(anchorKey).map((r) => (
              <button
                key={r}
                onClick={() => link(picking, r)}
                className="min-h-11 rounded-lg border-2 border-(--line) bg-(--bg) px-3 text-sm hover:border-(--accent)"
              >
                {REL_BUTTON[r](timeName)}
              </button>
            ))}
            <button onClick={() => setPicking(null)} className="min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3 text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
